import { useEffect, useRef, useState } from "react";
import type { FunctionDef, IntentSummary, MacroScope, ScopeOptions, WorkType } from "../types";
import { options, sorted } from "./ScopePicker";

export const EMPTY_MACRO: MacroScope = { functions: [], family: "", group1: "", group2: "" };

const macroKey = (boardId: string) => `gentik.macro.${boardId}`;
export function loadMacro(boardId: string): MacroScope {
  try {
    const v = JSON.parse(localStorage.getItem(macroKey(boardId)) || "null");
    if (v && Array.isArray(v.functions)) return { ...EMPTY_MACRO, ...v };
  } catch { /* storage blocked or bad value */ }
  return EMPTY_MACRO;
}
export function storeMacro(boardId: string, m: MacroScope) {
  try { localStorage.setItem(macroKey(boardId), JSON.stringify(m)); } catch { /* ignore */ }
}

export const isMacroEmpty = (m: MacroScope) => !m.functions.length && !m.family;

/**
 * Does a card fall inside the session scope? An intent scoped wider than the filter
 * (e.g. "all families") overlaps it, so it counts as inside.
 */
export function inMacro(intent: IntentSummary, m: MacroScope, workTypes: Record<string, WorkType>): boolean {
  const fn = workTypes[intent.work_type]?.function ?? intent.function_hint ?? "";
  if (m.functions.length && fn && !m.functions.includes(fn)) return false;
  const s = intent.scope;
  if (m.family && s.family && s.family !== m.family) return false;
  if (m.group1 && s.group1 && s.group1 !== m.group1) return false;
  if (m.group2 && s.group2 && s.group2 !== m.group2) return false;
  return true;
}

interface Props {
  value: MacroScope;
  onChange: (m: MacroScope) => void;
  functions: FunctionDef[];
  intents: IntentSummary[];
  workTypes: Record<string, WorkType>;
}

/** Session filters in the top bar: functions (a menu) and the product hierarchy. */
export default function TopFilters({ value, onChange, functions, intents, workTypes }: Props) {
  const [opts, setOpts] = useState<ScopeOptions | null>(null);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => { options().then(setOpts).catch(() => undefined); }, []);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", esc); };
  }, [open]);

  const tree = opts?.hierarchy ?? {};
  const families = sorted(tree);
  const groups1 = value.family ? sorted(tree[value.family]) : [];
  const groups2 = value.family && value.group1 ? sorted(tree[value.family]?.[value.group1]) : [];

  const toggleFn = (id: string) => onChange({
    ...value,
    functions: value.functions.includes(id) ? value.functions.filter((f) => f !== id) : [...value.functions, id],
  });
  const countFor = (fnId: string) => intents.filter((i) => workTypes[i.work_type]?.function === fnId).length;
  const picked = functions.filter((f) => value.functions.includes(f.id));
  const fnText = !picked.length ? "All functions" : picked.length === 1 ? picked[0].label : `${picked.length} functions`;

  return (
    <div className="top-filters" role="group" aria-label="Filters">
      <div className="fn-menu" ref={menuRef}>
        <button type="button" className={`input sm fn-menu-btn${picked.length ? " active" : ""}`} aria-haspopup="true"
                aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {picked.length === 1 && <span className={`fn-dot fn-${picked[0].id}`} />}
          <span className="grow">{fnText}</span><span aria-hidden>▾</span>
        </button>
        {open && (
          <div className="fn-menu-panel" role="menu">
            {functions.map((f) => (
              <label key={f.id} className={`fn-option fn-${f.id}`} role="menuitemcheckbox" aria-checked={value.functions.includes(f.id)}>
                <input type="checkbox" checked={value.functions.includes(f.id)} onChange={() => toggleFn(f.id)} />
                <span className="fn-dot" />{f.label}<span className="n">{countFor(f.id)}</span>
              </label>
            ))}
            {picked.length > 0 && (
              <button className="btn ghost sm" onClick={() => onChange({ ...value, functions: [] })}>All functions</button>
            )}
          </div>
        )}
      </div>
      <select className="input sm" aria-label="Product family" value={value.family}
              onChange={(e) => onChange({ ...value, family: e.target.value, group1: "", group2: "" })}>
        <option value="">All families</option>
        {families.map((f) => <option key={f}>{f}</option>)}
      </select>
      <select className="input sm" aria-label="Product group 1" value={value.group1} disabled={!value.family}
              onChange={(e) => onChange({ ...value, group1: e.target.value, group2: "" })}>
        <option value="">All groups</option>
        {groups1.map((g) => <option key={g}>{g}</option>)}
      </select>
      <select className="input sm group2-select" aria-label="Product group 2" value={value.group2} disabled={!value.group1}
              onChange={(e) => onChange({ ...value, group2: e.target.value })}>
        <option value="">All groups</option>
        {groups2.map((g) => <option key={g}>{g}</option>)}
      </select>
      {!isMacroEmpty(value) && (
        <button className="btn ghost sm" onClick={() => onChange(EMPTY_MACRO)} title="Clear all filters">Clear</button>
      )}
    </div>
  );
}
